import { Navigate, redirect, useRouter } from '@tanstack/react-router';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import ActivityLogsPage from './index.page';
import type { ActivityLogTab } from './types';

/** The two taxonomy URL segments. `activity_logs` is the pre-rename (current) segment. */
export type ActivityLogsSegment = 'activity_logs' | 'profile_activity';

const TAB_ROUTES = {
  activity_logs: '/workspaces/$workspaceId/journeys/activity_logs/$tab',
  profile_activity: '/workspaces/$workspaceId/journeys/profile_activity/$tab',
} as const;

/**
 * Index `beforeLoad`: land on this segment's own default (`identified`) tab. Deliberately
 * static — no feature-flag read — so it can't race the account load. Segment
 * canonicalization (activity_logs ↔ profile_activity) happens in the tab view below, after
 * the rename stage is known.
 */
export function redirectToSegmentDefaultTab(
  segment: ActivityLogsSegment,
  params: { workspaceId?: WorkspaceIdRouteParamInput },
): void {
  throw redirect({
    to: TAB_ROUTES[segment],
    // The workspace route's params.stringify normalizes the id when the location is built.
    params: { workspaceId: params.workspaceId ?? 'last', tab: 'identified' },
    search: true,
  });
}

/**
 * Renders the Activity Logs page at whichever segment is canonical for the account, and
 * forwards the non-canonical segment to it (a flag-aware redirect).
 *
 * The redirect lives here (not in `beforeLoad`) on purpose: `beforeLoad` runs before
 * `accountStore` is populated on a cold load, so a flag read there races and disagrees with
 * the rendered title/nav. Reading the rename stage from `useProductTaxonomy` — the same
 * source as the title and nav — keeps the URL vocabulary and the visible copy consistent.
 */
type ActivityLogsRouteViewProps = {
  segment: ActivityLogsSegment;
  workspaceId: WorkspaceIdRouteParamInput;
  tab: string;
};

export function ActivityLogsRouteView({ segment, workspaceId: workspaceIdParam, tab }: ActivityLogsRouteViewProps) {
  const taxonomy = useProductTaxonomy();
  const router = useRouter();
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  const canonicalSegment: ActivityLogsSegment = taxonomy.variant === 'future' ? 'profile_activity' : 'activity_logs';
  const currentTab: ActivityLogTab = tab === 'anonymous' ? 'anonymous' : 'identified';

  if (segment !== canonicalSegment) {
    return <Navigate to={TAB_ROUTES[canonicalSegment]} params={{ workspaceId, tab: currentTab }} search replace />;
  }

  // Build each tab's href on the canonical segment, preserving the current filters (search params).
  // `TabNav` renders these as real links and routes them through the app router (client-side nav).
  const tabHref = (next: ActivityLogTab) =>
    router.buildLocation({
      to: TAB_ROUTES[canonicalSegment],
      params: { workspaceId, tab: next },
      search: (prev) => prev,
    }).href;

  return <ActivityLogsPage workspaceId={workspaceId} tab={currentTab} tabHref={tabHref} />;
}
